import React, { useRef, useEffect, useCallback, useState } from 'react';
import { Upload, Pencil, Check, ArrowDownRight, Trash2, Image as ImageIcon, Undo2, Redo2, FlipHorizontal, FlipVertical } from 'lucide-react';
import { cn } from '@/lib/utils';
import { FILTERS } from './FiltersPanel';
import { TextItem } from './TextOverlay';
import { StickerItem } from './StickerOverlay';
import { ImageTransform } from './ImageEditor';
import { ImageAdjustments } from './AdjustmentsPanel';
import { useLanguage } from '@/contexts/LanguageContext';
import { useIsMobile } from '@/hooks/use-mobile';
import { Button } from '@/components/ui/button';
import ImageEditPortal from './ImageEditPortal';

interface GridCell {
  id: string;
  image: string | null;
  span?: { col?: number; row?: number };
  transform?: ImageTransform;
}

interface CollageGridProps {
  template: GridCell[];
  columns: number;
  gap: number;
  borderRadius: number;
  backgroundColor: string;
  backgroundImage: string | null;
  canvasBorder: number;
  cellBorder: number;
  borderColor: string;
  canvasWidth: number;
  canvasHeight: number;
  filter: string;
  adjustments: ImageAdjustments;
  textItems: TextItem[];
  stickerItems: StickerItem[];
  editingCellId: string | null;
  onCellClick: (cellId: string) => void;
  onImageDrop: (cellId: string, file: File) => void;
  onRemoveImage: (cellId: string) => void;
  onTextDrag: (id: string, x: number, y: number) => void;
  onStickerDrag: (id: string, x: number, y: number) => void;
  onEditCell: (cellId: string | null) => void;
  onReplaceImage: (cellId: string) => void;
  onFlipH: (cellId: string) => void;
  onFlipV: (cellId: string) => void;
  onUndo: (cellId: string) => void;
  onRedo: (cellId: string) => void;
  canUndo: (cellId: string) => boolean;
  canRedo: (cellId: string) => boolean;
  onSetImageScale: (cellId: string, newScale: number) => void;
  onMoveImageDrag: (cellId: string, offsetX: number, offsetY: number) => void;
  onUpdateText: (id: string, updates: Partial<TextItem>) => void;
  onRemoveText: (id: string) => void;
  onRemoveSticker: (id: string) => void;
  onUpdateSticker: (id: string, updates: Partial<StickerItem>) => void;
  onPreviewResize?: (width: number) => void;
}

const CollageGrid: React.FC<CollageGridProps> = ({
  template,
  columns,
  gap,
  borderRadius,
  backgroundColor,
  backgroundImage,
  canvasBorder,
  cellBorder,
  borderColor,
  canvasWidth,
  canvasHeight,
  filter,
  adjustments,
  textItems,
  stickerItems,
  editingCellId,
  onCellClick,
  onImageDrop,
  onRemoveImage,
  onTextDrag,
  onStickerDrag,
  onEditCell,
  onReplaceImage,
  onFlipH,
  onFlipV,
  onUndo,
  onRedo,
  canUndo,
  canRedo,
  onSetImageScale,
  onMoveImageDrag,
  onUpdateText,
  onRemoveText,
  onRemoveSticker,
  onUpdateSticker,
  onPreviewResize,
}) => {
  const { t } = useLanguage();
  const isMobile = useIsMobile();
  const canvasRef = useRef<HTMLDivElement>(null);
  const cellRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const imageRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const [editingTextId, setEditingTextId] = useState<string | null>(null);
  const [editingTextValue, setEditingTextValue] = useState('');
  const [draggingTextId, setDraggingTextId] = useState<string | null>(null);
  const [selectedTextId, setSelectedTextId] = useState<string | null>(null);
  const [draggingStickerId, setDraggingStickerId] = useState<string | null>(null);
  const [selectedStickerId, setSelectedStickerId] = useState<string | null>(null);
  const [editingCellRect, setEditingCellRect] = useState<DOMRect | null>(null);
  const dragStateRef = useRef<{ cellId: string; offsetX: number; offsetY: number; scale: number } | null>(null);
  
  // Force re-render when cells are ready (for image positioning)
  const [cellsReady, setCellsReady] = useState(0);
  
  // Track if hint has been shown (stored in localStorage permanently)
  const HINT_STORAGE_KEY = 'collage_edit_hint_shown';
  const [showHint, setShowHint] = useState(false);
  
  // Use ResizeObserver to track canvas width without causing forced reflow
  useEffect(() => {
    if (!onPreviewResize || !canvasRef.current) return;
    
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        onPreviewResize(entry.contentRect.width);
      }
    });
    
    observer.observe(canvasRef.current);
    // Initial call
    onPreviewResize(canvasRef.current.offsetWidth);
    
    return () => observer.disconnect();
  }, [onPreviewResize]);
  
  // Trigger re-render when cells mount
  useEffect(() => {
    const timer = setTimeout(() => setCellsReady(prev => prev + 1), 50);
    return () => clearTimeout(timer);
  }, [template]);
  
  // When a cell with image starts editing, show hint once ever
  useEffect(() => {
    if (editingCellId) {
      const cell = template.find(c => c.id === editingCellId);
      let hasShownHint = false;
      try {
        hasShownHint = localStorage.getItem(HINT_STORAGE_KEY) === 'true';
      } catch {}
      
      if (cell?.image && !hasShownHint) {
        try {
          localStorage.setItem(HINT_STORAGE_KEY, 'true');
        } catch {}
        setShowHint(true);
        setTimeout(() => {
          setShowHint(false);
        }, 3000);
      }
    }
  }, [editingCellId, template]);

  // Update cell rect when editing starts or window resizes
  useEffect(() => {
    if (editingCellId) {
      const updateRect = () => {
        const cellEl = cellRefs.current.get(editingCellId);
        if (cellEl) {
          const rect = cellEl.getBoundingClientRect();
          console.log('[ImageEdit] Cell rect:', { 
            cellId: editingCellId,
            left: rect.left, 
            top: rect.top, 
            width: rect.width, 
            height: rect.height 
          });
          setEditingCellRect(rect);
        } else {
          console.log('[ImageEdit] Cell element not found for:', editingCellId);
        }
      };
      // Small delay to ensure layout is complete
      setTimeout(updateRect, 50);
      window.addEventListener('resize', updateRect);
      window.addEventListener('scroll', updateRect);
      return () => {
        window.removeEventListener('resize', updateRect);
        window.removeEventListener('scroll', updateRect);
      };
    } else {
      setEditingCellRect(null);
    }
  }, [editingCellId]);

  // Handle wheel zoom with native event to properly prevent scroll
  const handleWheelZoom = useCallback((e: WheelEvent, cellId: string, currentTransform?: ImageTransform) => {
    e.preventDefault();
    e.stopPropagation();
    
    const currentScale = currentTransform?.scale || 1;
    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    const newScale = Math.min(3, Math.max(0.3, currentScale + delta));
    
    onSetImageScale(cellId, newScale);
  }, [onSetImageScale]);

  // Attach native wheel listeners with { passive: false } to allow preventDefault
  useEffect(() => {
    const handlers = new Map<HTMLDivElement, (e: WheelEvent) => void>();
    
    template.forEach(cell => {
      const element = cellRefs.current.get(cell.id);
      if (element && editingCellId === cell.id && cell.image) {
        const handler = (e: WheelEvent) => handleWheelZoom(e, cell.id, cell.transform);
        handlers.set(element, handler);
        element.addEventListener('wheel', handler, { passive: false });
      }
    });

    return () => {
      handlers.forEach((handler, element) => {
        element.removeEventListener('wheel', handler);
      });
    };
  }, [template, editingCellId, handleWheelZoom]);

  const handleDrop = (e: React.DragEvent, cellId: string) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) {
      onImageDrop(cellId, file);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const filterStyle = FILTERS.find(f => f.id === filter)?.filter || '';
  
  // Build combined filter including adjustments
  const adjustmentStyle = `brightness(${adjustments.brightness / 100}) contrast(${adjustments.contrast / 100}) saturate(${adjustments.saturation / 100})`;
  const combinedFilter = [filterStyle, adjustmentStyle].filter(Boolean).join(' ');
  
  const aspectRatio = canvasWidth / canvasHeight;

  // Unified handler for text dragging (mouse + touch) with throttling for mobile performance
  const handleTextDragStart = (clientX: number, clientY: number, textId: string) => {
    const canvas = document.getElementById('collage-canvas');
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const startX = clientX;
    const startY = clientY;
    const textItem = textItems.find(t => t.id === textId);
    if (!textItem) return;

    const startTextX = textItem.x;
    const startTextY = textItem.y;
    
    // Throttle for mobile performance
    let lastUpdate = 0;
    const throttleMs = 16; // ~60fps
    let pendingX = startTextX;
    let pendingY = startTextY;
    let rafId: number | null = null;

    const applyUpdate = () => {
      onTextDrag(textId, pendingX, pendingY);
      rafId = null;
    };

    const handleMove = (moveX: number, moveY: number) => {
      const now = performance.now();
      if (now - lastUpdate < throttleMs) return;
      lastUpdate = now;
      
      const deltaX = ((moveX - startX) / rect.width) * 100;
      const deltaY = ((moveY - startY) / rect.height) * 100;
      pendingX = Math.max(10, Math.min(90, startTextX + deltaX));
      pendingY = Math.max(10, Math.min(90, startTextY + deltaY));
      
      // Use requestAnimationFrame for smooth updates
      if (!rafId) {
        rafId = requestAnimationFrame(applyUpdate);
      }
    };

    const handleMouseMove = (moveEvent: MouseEvent) => {
      handleMove(moveEvent.clientX, moveEvent.clientY);
    };

    const handleTouchMove = (moveEvent: TouchEvent) => {
      moveEvent.preventDefault();
      const touch = moveEvent.touches[0];
      handleMove(touch.clientX, touch.clientY);
    };

    const handleEnd = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleEnd);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleEnd);
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
      // Final update and show confirmation button
      onTextDrag(textId, pendingX, pendingY);
      setDraggingTextId(textId);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleEnd);
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleEnd);
  };

  const handleTextMouseDown = (e: React.MouseEvent, textId: string) => {
    e.stopPropagation();
    handleTextDragStart(e.clientX, e.clientY, textId);
  };

  const handleTextTouchStart = (e: React.TouchEvent, textId: string) => {
    e.stopPropagation();
    e.preventDefault();
    const touch = e.touches[0];
    const startX = touch.clientX;
    const startY = touch.clientY;
    
    // Add small delay before starting drag to prevent "jump" and show menu first
    const touchTimeout = setTimeout(() => {
      handleTextDragStart(startX, startY, textId);
    }, 150);
    
    // If touch ends quickly, it's a tap - just select, don't drag
    const handleTouchEnd = () => {
      clearTimeout(touchTimeout);
      document.removeEventListener('touchend', handleTouchEnd);
    };
    
    document.addEventListener('touchend', handleTouchEnd, { once: true });
  };

  // Unified handler for text resizing (mouse + touch)
  const handleTextResizeStart = (clientX: number, clientY: number, textItem: TextItem) => {
    const startX = clientX;
    const startY = clientY;
    const startSize = textItem.size;

    const handleMove = (moveX: number, moveY: number) => {
      const deltaX = moveX - startX;
      const deltaY = moveY - startY;
      const avgDelta = (deltaX + deltaY) / 2;
      const newSize = Math.max(8, Math.min(200, startSize + avgDelta / 2));
      onUpdateText(textItem.id, { size: Math.round(newSize) });
    };

    const handleMouseMove = (moveEvent: MouseEvent) => {
      handleMove(moveEvent.clientX, moveEvent.clientY);
    };

    const handleTouchMove = (moveEvent: TouchEvent) => {
      moveEvent.preventDefault();
      const touch = moveEvent.touches[0];
      handleMove(touch.clientX, touch.clientY);
    };

    const handleEnd = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleEnd);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleEnd);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleEnd);
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleEnd);
  };

  // Unified handler for sticker resizing (mouse + touch)
  const handleStickerResizeStart = (clientX: number, clientY: number, sticker: StickerItem) => {
    const startX = clientX;
    const startY = clientY;
    const startScale = sticker.scale;

    const handleMove = (moveX: number, moveY: number) => {
      const deltaX = moveX - startX;
      const deltaY = moveY - startY;
      const avgDelta = (deltaX + deltaY) / 2;
      const newScale = Math.max(0.1, Math.min(3, startScale + avgDelta / 100));
      onUpdateSticker(sticker.id, { scale: newScale });
    };

    const handleMouseMove = (moveEvent: MouseEvent) => {
      handleMove(moveEvent.clientX, moveEvent.clientY);
    };

    const handleTouchMove = (moveEvent: TouchEvent) => {
      moveEvent.preventDefault();
      const touch = moveEvent.touches[0];
      handleMove(touch.clientX, touch.clientY);
    };

    const handleEnd = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleEnd);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleEnd);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleEnd);
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleEnd);
  };

  // Unified handler for scaling (mouse + touch)
  const handleScaleStart = (
    clientX: number,
    clientY: number,
    cellId: string, 
    corner: 'tl' | 'tr' | 'bl' | 'br',
    currentTransform?: ImageTransform
  ) => {
    const startX = clientX;
    const startY = clientY;
    const startScale = currentTransform?.scale || 1;
    
    // Direction multipliers based on corner
    const directions: Record<string, { x: number; y: number }> = {
      tl: { x: -1, y: -1 },
      tr: { x: 1, y: -1 },
      bl: { x: -1, y: 1 },
      br: { x: 1, y: 1 },
    };
    const dir = directions[corner];

    let currentScale = startScale;
    
    const handleMove = (moveClientX: number, moveClientY: number) => {
      const deltaX = (moveClientX - startX) * dir.x;
      const deltaY = (moveClientY - startY) * dir.y;
      const avgDelta = (deltaX + deltaY) / 2;
      
      const scaleDelta = avgDelta / 100;
      currentScale = Math.min(3, Math.max(0.3, startScale + scaleDelta));
      
      const transform = currentTransform || { flipH: false, flipV: false, offsetX: 0, offsetY: 0, scale: 1 };
      const transforms: string[] = [];
      if (transform.flipH) transforms.push('scaleX(-1)');
      if (transform.flipV) transforms.push('scaleY(-1)');
      transforms.push(`scale(${currentScale})`);
      if (transform.offsetX || transform.offsetY) {
        transforms.push(`translate(${transform.offsetX}%, ${transform.offsetY}%)`);
      }
      const transformStr = transforms.join(' ');
      
      const img1 = imageRefs.current.get(cellId);
      const img2 = imageRefs.current.get(`${cellId}-clip`);
      if (img1) img1.style.transform = transformStr;
      if (img2) img2.style.transform = transformStr;
    };

    const handleMouseMove = (moveEvent: MouseEvent) => {
      handleMove(moveEvent.clientX, moveEvent.clientY);
    };
    
    const handleTouchMove = (moveEvent: TouchEvent) => {
      moveEvent.preventDefault();
      const touch = moveEvent.touches[0];
      handleMove(touch.clientX, touch.clientY);
    };

    const handleEnd = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleEnd);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleEnd);
      onSetImageScale(cellId, currentScale);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleEnd);
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleEnd);
  };

  const handleScaleArrowMouseDown = (
    e: React.MouseEvent, 
    cellId: string, 
    corner: 'tl' | 'tr' | 'bl' | 'br',
    currentTransform?: ImageTransform
  ) => {
    e.preventDefault();
    e.stopPropagation();
    handleScaleStart(e.clientX, e.clientY, cellId, corner, currentTransform);
  };

  const handleScaleArrowTouchStart = (
    e: React.TouchEvent, 
    cellId: string, 
    corner: 'tl' | 'tr' | 'bl' | 'br',
    currentTransform?: ImageTransform
  ) => {
    e.preventDefault();
    e.stopPropagation();
    const touch = e.touches[0];
    handleScaleStart(touch.clientX, touch.clientY, cellId, corner, currentTransform);
  };

  // Unified handler for image dragging (mouse + touch with pinch-to-zoom)
  const handleImageDragStart = (
    clientX: number,
    clientY: number,
    cell: { id: string; transform?: ImageTransform },
    initialTouchCount?: number,
    initialPinchDist?: number
  ) => {
    console.log('[DEBUG] handleImageDragStart', {
      cellId: cell.id,
      transform: cell.transform,
      clientX,
      clientY,
      initialTouchCount,
    });
    
    const startX = clientX;
    const startY = clientY;
    const startOffsetX = cell.transform?.offsetX || 0;
    const startOffsetY = cell.transform?.offsetY || 0;
    const startScale = cell.transform?.scale || 1;
    const cellElement = cellRefs.current.get(cell.id);
    const cellWidth = cellElement?.offsetWidth || 200;
    const cellHeight = cellElement?.offsetHeight || 200;
    
    console.log('[DEBUG] cell dimensions', { cellWidth, cellHeight, startScale, startOffsetX, startOffsetY });
    
    let currentOffsetX = startOffsetX;
    let currentOffsetY = startOffsetY;
    let currentScale = startScale;
    let isPinching = (initialTouchCount || 0) >= 2;
    let initialPinchDistance = initialPinchDist || 0;

    const updateTransform = () => {
      const transform = cell.transform || { flipH: false, flipV: false, offsetX: 0, offsetY: 0, scale: 1 };
      const transforms: string[] = [];
      if (transform.flipH) transforms.push('scaleX(-1)');
      if (transform.flipV) transforms.push('scaleY(-1)');
      transforms.push(`scale(${currentScale})`);
      transforms.push(`translate(${currentOffsetX}%, ${currentOffsetY}%)`);
      const transformStr = transforms.join(' ');
      
      const img1 = imageRefs.current.get(cell.id);
      const img2 = imageRefs.current.get(`${cell.id}-clip`);
      if (img1) img1.style.transform = transformStr;
      if (img2) img2.style.transform = transformStr;
    };
    
    const handleMouseMove = (moveEvent: MouseEvent) => {
      let deltaX = ((moveEvent.clientX - startX) / cellWidth) * 100;
      let deltaY = ((moveEvent.clientY - startY) / cellHeight) * 100;
      // Инвертируем направление при отражении изображения
      if (cell.transform?.flipH) deltaX = -deltaX;
      if (cell.transform?.flipV) deltaY = -deltaY;
      // Ограничиваем смещение изображения чтобы не выходило слишком далеко за пределы ячейки
      currentOffsetX = Math.max(-50, Math.min(50, startOffsetX + deltaX));
      currentOffsetY = Math.max(-50, Math.min(50, startOffsetY + deltaY));
      updateTransform();
    };
    
    const handleTouchMove = (moveEvent: TouchEvent) => {
      moveEvent.preventDefault();
      
      if (moveEvent.touches.length === 2) {
        // Pinch-to-zoom
        isPinching = true;
        const dx = moveEvent.touches[0].clientX - moveEvent.touches[1].clientX;
        const dy = moveEvent.touches[0].clientY - moveEvent.touches[1].clientY;
        const currentDistance = Math.sqrt(dx * dx + dy * dy);
        
        if (initialPinchDistance === 0) {
          initialPinchDistance = currentDistance;
        }
        
        const scaleFactor = currentDistance / initialPinchDistance;
        currentScale = Math.min(3, Math.max(0.3, startScale * scaleFactor));
        
        // Also track center point movement for panning while pinching
        const centerX = (moveEvent.touches[0].clientX + moveEvent.touches[1].clientX) / 2;
        const centerY = (moveEvent.touches[0].clientY + moveEvent.touches[1].clientY) / 2;
        let deltaX = ((centerX - startX) / cellWidth) * 100;
        let deltaY = ((centerY - startY) / cellHeight) * 100;
        // Инвертируем направление при отражении изображения
        if (cell.transform?.flipH) deltaX = -deltaX;
        if (cell.transform?.flipV) deltaY = -deltaY;
        // Ограничиваем смещение при pinch-zoom
        currentOffsetX = Math.max(-50, Math.min(50, startOffsetX + deltaX));
        currentOffsetY = Math.max(-50, Math.min(50, startOffsetY + deltaY));
      } else if (moveEvent.touches.length === 1 && !isPinching) {
        // Single finger drag
        const touch = moveEvent.touches[0];
        let deltaX = ((touch.clientX - startX) / cellWidth) * 100;
        let deltaY = ((touch.clientY - startY) / cellHeight) * 100;
        // Инвертируем направление при отражении изображения
        if (cell.transform?.flipH) deltaX = -deltaX;
        if (cell.transform?.flipV) deltaY = -deltaY;
        // Ограничиваем смещение изображения
        currentOffsetX = Math.max(-50, Math.min(50, startOffsetX + deltaX));
        currentOffsetY = Math.max(-50, Math.min(50, startOffsetY + deltaY));
      }
      
      updateTransform();
    };

    const handleEnd = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleEnd);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleEnd);
      onMoveImageDrag(cell.id, currentOffsetX, currentOffsetY);
      if (currentScale !== startScale) {
        onSetImageScale(cell.id, currentScale);
      }
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleEnd);
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleEnd);
  };

  // Wheel zoom is handled by native event listener above

  /**
   * ПОДХОД КОНКУРЕНТА (mycollages.ru):
   * 
   * Вычисляем размеры и позицию изображения для отображения
   * offsetX/offsetY - смещение от центра в процентах от размера ячейки
   */
  const getImageLayout = (
    cellElement: HTMLElement | undefined,
    transform?: ImageTransform
  ): { width: number; height: number; left: number; top: number; flipTransform?: string } => {
    if (!cellElement) {
      return { width: 0, height: 0, left: 0, top: 0 };
    }
    
    const cellW = cellElement.offsetWidth;
    const cellH = cellElement.offsetHeight;
    const cellRatio = cellW / cellH;
    
    const scale = transform?.scale || 1;
    const aspectRatio = transform?.aspectRatio || 1;
    const offsetX = transform?.offsetX || 0;
    const offsetY = transform?.offsetY || 0;
    
    // Cover: изображение покрывает всю ячейку
    let baseWidth: number;
    let baseHeight: number;
    
    if (aspectRatio > cellRatio) {
      // Изображение шире ячейки - подгоняем по высоте
      baseHeight = cellH;
      baseWidth = cellH * aspectRatio;
    } else {
      // Изображение выше ячейки - подгоняем по ширине
      baseWidth = cellW;
      baseHeight = cellW / aspectRatio;
    }
    
    // Применяем масштаб
    const width = baseWidth * scale;
    const height = baseHeight * scale;
    
    // Центрированная позиция
    const centerX = (cellW - width) / 2;
    const centerY = (cellH - height) / 2;
    
    // Смещение в пикселях
    const pixelOffsetX = (offsetX / 100) * cellW;
    const pixelOffsetY = (offsetY / 100) * cellH;
    
    // Flip transform
    const flipParts: string[] = [];
    if (transform?.flipH) flipParts.push('scaleX(-1)');
    if (transform?.flipV) flipParts.push('scaleY(-1)');
    
    return {
      width,
      height,
      left: centerX + pixelOffsetX,
      top: centerY + pixelOffsetY,
      flipTransform: flipParts.length > 0 ? flipParts.join(' ') : undefined,
    };
  };

  // Calculate positions for 4 corner arrows based on image transform
  // Clamp positions to stay within reasonable bounds so arrows remain visible
  const getArrowPositions = (transform?: ImageTransform) => {
    const scale = transform?.scale || 1;
    // offsetX/offsetY уже в процентах от ячейки (примерно 1% = 10px движения мыши)
    const offsetX = (transform?.offsetX || 0);
    const offsetY = (transform?.offsetY || 0);
    
    // Image bounds (in percentage of cell)
    const halfW = 50 * scale;
    const halfH = 50 * scale;
    const centerX = 50 + offsetX;
    const centerY = 50 + offsetY;
    
    // Clamp function to keep arrows strictly inside cell bounds (8% to 92%)
    const clamp = (val: number) => Math.max(8, Math.min(92, val));
    
    return {
      // Corners (for scaling) - clamped to stay visible
      tl: { x: clamp(centerX - halfW), y: clamp(centerY - halfH) },
      tr: { x: clamp(centerX + halfW), y: clamp(centerY - halfH) },
      bl: { x: clamp(centerX - halfW), y: clamp(centerY + halfH) },
      br: { x: clamp(centerX + halfW), y: clamp(centerY + halfH) },
    };
  };

  // Calculate actual number of rows using the same algorithm as export
  const calculatedRows = React.useMemo(() => {
    const gridMap: (string | null)[][] = [];
    let maxRow = 0;
    
    for (const cell of template) {
      const spanCol = cell.span?.col || 1;
      const spanRow = cell.span?.row || 1;
      
      // Find first available position for this cell
      let placed = false;
      for (let r = 0; !placed; r++) {
        // Ensure row exists in gridMap
        while (gridMap.length <= r + spanRow - 1) {
          gridMap.push(new Array(columns).fill(null));
        }
        
        for (let c = 0; c <= columns - spanCol; c++) {
          // Check if all required cells are free
          let canPlace = true;
          for (let dr = 0; dr < spanRow && canPlace; dr++) {
            for (let dc = 0; dc < spanCol && canPlace; dc++) {
              if (gridMap[r + dr][c + dc] !== null) {
                canPlace = false;
              }
            }
          }
          
          if (canPlace) {
            // Place this cell
            for (let dr = 0; dr < spanRow; dr++) {
              for (let dc = 0; dc < spanCol; dc++) {
                gridMap[r + dr][c + dc] = cell.id;
              }
            }
            maxRow = Math.max(maxRow, r + spanRow);
            placed = true;
            break;
          }
        }
      }
    }
    
    return maxRow || Math.ceil(template.length / columns);
  }, [template, columns]);

  return (
    <>
    <div
      ref={canvasRef}
      id="collage-canvas"
      className="w-full mx-auto transition-all duration-300 relative overflow-hidden"
      style={{
        aspectRatio: `${aspectRatio}`,
        maxWidth: '100%',
        background: borderColor,
        padding: `${canvasBorder}px`,
        filter: combinedFilter,
        // Allow vertical scroll on mobile, but block horizontal pan and pinch when not editing
        touchAction: editingCellId ? 'none' : 'pan-y',
      }}
      onClick={() => {
        // Закрываем выбор текста и стикера при клике на холст
        setSelectedTextId(null);
        setDraggingTextId(null);
        setSelectedStickerId(null);
        if (!editingCellId) {
          onEditCell(null);
        }
      }}
    >
      {/* Grid layer - borderColor shows through gaps */}
      <div
        className="w-full h-full relative"
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${columns}, 1fr)`,
          gridTemplateRows: `repeat(${calculatedRows}, 1fr)`,
          gap: `${gap}px`,
          background: borderColor,
        }}
      >
        {template.map((cell, index) => {
          const isEditing = editingCellId === cell.id;
          
          return (
            <div
              key={cell.id}
              ref={(el) => {
                if (el) cellRefs.current.set(cell.id, el);
                else cellRefs.current.delete(cell.id);
              }}
              className={cn(
                "relative group cursor-pointer transition-all duration-200 overflow-hidden",
                isEditing ? "z-20" : "",
                editingCellId && !isEditing ? "pointer-events-none opacity-30" : "",
                selectedTextId ? "pointer-events-none" : ""
              )}
              style={{
                borderRadius: `${borderRadius}px`,
                gridColumn: cell.span?.col ? `span ${cell.span.col}` : undefined,
                gridRow: cell.span?.row ? `span ${cell.span.row}` : undefined,
                animationDelay: `${index * 50}ms`,
                border: cellBorder > 0 ? `${cellBorder}px solid ${borderColor}` : undefined,
                background: backgroundImage ? `url(${backgroundImage}) center/cover` : backgroundColor,
              }}
              onClick={(e) => {
                e.stopPropagation();
                // Сбрасываем выбор стикера при клике на ячейку
                setSelectedStickerId(null);
                // Блокируем клики на другие ячейки при редактировании
                if (editingCellId && !isEditing) return;
                if (!cell.image) {
                  onCellClick(cell.id);
                }
              }}
              onDrop={(e) => handleDrop(e, cell.id)}
              onDragOver={handleDragOver}
            >
              {cell.image ? (
                <>
                  {/* Normal view - подход конкурента с <img> и left/top */}
                  {!isEditing && (() => {
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    const _forceUpdate = cellsReady; // Trigger re-render when cells mount
                    const cellEl = cellRefs.current.get(cell.id);
                    const layout = getImageLayout(cellEl, cell.transform);
                    
                    // Fallback если ячейка еще не готова
                    if (!cellEl || layout.width === 0) {
                      return (
                        <div 
                          className="absolute inset-0 overflow-hidden"
                          style={{ borderRadius: `${borderRadius}px` }}
                        >
                          <div
                            className="absolute inset-0 w-full h-full pointer-events-none"
                            style={{
                              backgroundImage: `url(${cell.image})`,
                              backgroundSize: 'cover',
                              backgroundPosition: 'center',
                            }}
                          />
                        </div>
                      );
                    }
                    
                    return (
                      <div 
                        className="absolute inset-0 overflow-hidden"
                        style={{ borderRadius: `${borderRadius}px` }}
                      >
                        {/* draggableHelper - позиционируется через left/top */}
                        <div
                          ref={(el) => {
                            if (el) imageRefs.current.set(cell.id, el);
                            else imageRefs.current.delete(cell.id);
                          }}
                          className="absolute pointer-events-none"
                          style={{
                            left: layout.left,
                            top: layout.top,
                            width: layout.width,
                            height: layout.height,
                          }}
                        >
                          <img
                            src={cell.image}
                            alt=""
                            draggable={false}
                            className="w-full h-full"
                            style={{
                              objectFit: 'fill',
                              transform: layout.flipTransform,
                            }}
                          />
                        </div>
                      </div>
                    );
                  })()}
                  
                  {/* Edit button (when not editing) */}
                  {!isEditing && !editingTextId && !draggingTextId && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onEditCell(cell.id);
                      }}
                      className="absolute top-2 right-2 p-2 rounded-lg bg-card/90 hover:bg-card border border-border shadow-lg opacity-0 group-hover:opacity-100 transition-opacity z-10"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                  )}

                  {/* Hint inside cell - shows once per session */}
                  {isEditing && showHint && (
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
                      <div className="flex items-center gap-2 px-3 py-2 bg-black/60 text-white rounded-lg text-sm animate-fade-in">
                        <span>{isMobile ? (t.imageHintDragZoomMobile || 'Перетащите для перемещения') : t.imageHintDragZoom}</span>
                      </div>
                    </div>
                  )}

                  {/* Portal-based image editor */}
                  {isEditing && editingCellRect && (
                    <ImageEditPortal
                      cell={{
                        id: cell.id,
                        image: cell.image!,
                        transform: cell.transform,
                      }}
                      cellRect={editingCellRect}
                      onClose={() => onEditCell(null)}
                      onReplace={() => onReplaceImage(cell.id)}
                      onDelete={() => onRemoveImage(cell.id)}
                      onFlipH={() => onFlipH(cell.id)}
                      onFlipV={() => onFlipV(cell.id)}
                      onUndo={() => onUndo(cell.id)}
                      onRedo={() => onRedo(cell.id)}
                      canUndo={canUndo(cell.id)}
                      canRedo={canRedo(cell.id)}
                      onSetScale={(scale) => onSetImageScale(cell.id, scale)}
                      onMoveImage={(ox, oy) => onMoveImageDrag(cell.id, ox, oy)}
                    />
                  )}
                </>
              ) : (
                <div 
                  className={cn(
                    "w-full h-full flex flex-col items-center justify-center gap-3 transition-all duration-200 bg-[#d1d5db] dark:bg-slate-800/60",
                    draggingTextId ? "pointer-events-none opacity-50" : "group-hover:brightness-95"
                  )}
                >
                  <div className={cn(
                    "flex flex-col items-center justify-center gap-2 px-4 py-3 rounded-lg transition-all duration-200",
                    !draggingTextId && "group-hover:bg-white/60 dark:group-hover:bg-white/10"
                  )}>
                    <div className={cn(
                      "w-10 h-10 rounded-full border-2 border-dashed border-slate-500 dark:border-slate-400 flex items-center justify-center transition-colors",
                      !draggingTextId && "group-hover:border-primary"
                    )}>
                      <Upload className={cn(
                        "w-5 h-5 text-slate-600 dark:text-slate-300 transition-colors",
                        !draggingTextId && "group-hover:text-primary"
                      )} />
                    </div>
                    <span className={cn(
                      "text-sm font-semibold text-slate-700 dark:text-slate-200 transition-colors",
                      !draggingTextId && "group-hover:text-slate-900 dark:group-hover:text-white"
                    )}>
                      {t.upload}
                    </span>
                  </div>
                </div>
              )}
            </div>
          );
        })}

      {/* Text overlays - only render if text is not empty */}
      {textItems.filter(t => t.text && t.text.trim().length > 0).map((text) => {
        const isEditingThis = editingTextId === text.id;
        
        return (
          <div
            key={text.id}
            className="absolute select-none group touch-none"
            style={{
              left: `${Math.max(10, Math.min(90, text.x))}%`,
              top: `${Math.max(10, Math.min(90, text.y))}%`,
              transform: 'translate(-50%, -50%)',
              touchAction: 'none',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {isEditingThis ? (
              // Режим редактирования текста
              <div className="flex items-center gap-1 bg-card/95 rounded-lg p-1 shadow-lg border border-border">
                <textarea
                  value={editingTextValue}
                  onChange={(e) => {
                    setEditingTextValue(e.target.value);
                    // Update text in real-time while typing
                    onUpdateText(text.id, { text: e.target.value });
                  }}
                  className="px-2 py-1 text-sm bg-background border border-border rounded min-w-[150px] min-h-[60px] max-w-[300px] text-foreground resize"
                  style={{
                    fontFamily: text.font,
                  }}
                  autoFocus
                  rows={3}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                      setEditingTextId(null);
                    }
                  }}
                />
                <button
                  onClick={() => {
                    onRemoveText(text.id);
                    setEditingTextId(null);
                  }}
                  className="p-1.5 rounded hover:bg-destructive/20 text-destructive touch-manipulation"
                  title="Удалить"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => {
                    setEditingTextId(null);
                  }}
                  className="p-1.5 rounded bg-green-500 hover:bg-green-600 text-white touch-manipulation"
                  title="Готово"
                >
                  <Check className="w-4 h-4" />
                </button>
              </div>
            ) : (
              // Обычный режим отображения текста
              <div 
                className="relative touch-none" 
                style={{ touchAction: 'none' }}
                onClick={(e) => {
                  e.stopPropagation();
                  // Выбираем текст по клику и сбрасываем стикер
                  setSelectedTextId(text.id);
                  setSelectedStickerId(null);
                }}
              >
                <div
                  className={cn(
                    "cursor-move rounded px-1 touch-none select-none",
                    selectedTextId === text.id ? "ring-2 ring-primary" : "hover:ring-2 hover:ring-primary/50"
                  )}
                  style={{
                    fontFamily: text.font,
                    fontSize: `${text.size}px`,
                    color: text.color,
                    textShadow: text.shadow ? '2px 2px 4px rgba(0,0,0,0.5)' : undefined,
                    WebkitTextStroke: text.stroke ? `${text.strokeWidth}px ${text.strokeColor}` : undefined,
                    paintOrder: text.stroke ? 'stroke fill' : undefined,
                    whiteSpace: 'pre',
                    lineHeight: 1.2,
                    touchAction: 'none',
                    textAlign: text.textAlign || 'center',
                  }}
                  onMouseDown={(e) => handleTextMouseDown(e, text.id)}
                  onTouchStart={(e) => {
                    e.preventDefault();
                    setSelectedTextId(text.id);
                    handleTextTouchStart(e, text.id);
                  }}
                >
                  {text.text}
                </div>
                
                {/* Угловая стрелка для изменения размера - только когда текст выбран */}
                {selectedTextId === text.id && (
                  <div
                    className="absolute -bottom-2 -right-2 w-6 h-6 bg-white/90 hover:bg-white rounded-sm shadow-md cursor-nwse-resize flex items-center justify-center z-10 touch-none"
                    style={{ touchAction: 'none' }}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleTextResizeStart(e.clientX, e.clientY, text);
                    }}
                    onTouchStart={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      const touch = e.touches[0];
                      handleTextResizeStart(touch.clientX, touch.clientY, text);
                    }}
                  >
                    <ArrowDownRight className="w-4 h-4 text-gray-600" />
                  </div>
                )}
                
                {/* Меню текста для десктопа - над текстом */}
                {selectedTextId === text.id && !isMobile && (
                  <div className="absolute -top-10 left-1/2 -translate-x-1/2 flex gap-1 bg-card/95 rounded-lg p-1 shadow-lg border border-border z-20">
                    {draggingTextId === text.id && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setDraggingTextId(null);
                        }}
                        className="p-1.5 rounded bg-green-500 hover:bg-green-600 text-white touch-manipulation"
                        title="Подтвердить позицию"
                      >
                        <Check className="w-3.5 h-3.5" />
                      </button>
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setDraggingTextId(null);
                        setSelectedTextId(null);
                        setEditingTextId(text.id);
                        setEditingTextValue(text.text);
                      }}
                      className="p-1.5 rounded hover:bg-muted touch-manipulation"
                      title="Редактировать"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setDraggingTextId(null);
                        setSelectedTextId(null);
                        onRemoveText(text.id);
                      }}
                      className="p-1.5 rounded hover:bg-destructive/20 text-destructive touch-manipulation"
                      title="Удалить"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}

      {/* Sticker overlays */}
      {stickerItems.map((sticker) => {
        const isSelected = selectedStickerId === sticker.id && !selectedTextId && !editingCellId;
        
        return (
          <div
            key={sticker.id}
            className="absolute select-none cursor-move touch-none"
            style={{
              left: `${Math.max(5, Math.min(95, sticker.x))}%`,
              top: `${Math.max(5, Math.min(95, sticker.y))}%`,
              transform: `translate(-50%, -50%) scale(${sticker.scale}) rotate(${sticker.rotation}deg)`,
              touchAction: 'none',
              zIndex: isSelected ? 30 : 25,
            }}
            onClick={(e) => {
              e.stopPropagation();
              setSelectedStickerId(sticker.id);
              setSelectedTextId(null);
            }}
            onMouseDown={(e) => {
              e.preventDefault();
              setSelectedStickerId(sticker.id);
              setSelectedTextId(null);
              setDraggingStickerId(sticker.id);
              const startX = e.clientX;
              const startY = e.clientY;
              const startStickerX = sticker.x;
              const startStickerY = sticker.y;
              const canvas = canvasRef.current;
              if (!canvas) return;
              const rect = canvas.getBoundingClientRect();
              
              const handleMouseMove = (moveEvent: MouseEvent) => {
                const deltaX = ((moveEvent.clientX - startX) / rect.width) * 100;
                const deltaY = ((moveEvent.clientY - startY) / rect.height) * 100;
                onStickerDrag(sticker.id, startStickerX + deltaX, startStickerY + deltaY);
              };
              
              const handleMouseUp = () => {
                setDraggingStickerId(null);
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
              };
              
              document.addEventListener('mousemove', handleMouseMove);
              document.addEventListener('mouseup', handleMouseUp);
            }}
            onTouchStart={(e) => {
              e.preventDefault();
              setSelectedStickerId(sticker.id);
              setSelectedTextId(null);
              setDraggingStickerId(sticker.id);
              const touch = e.touches[0];
              const startX = touch.clientX;
              const startY = touch.clientY;
              const startStickerX = sticker.x;
              const startStickerY = sticker.y;
              const canvas = canvasRef.current;
              if (!canvas) return;
              const rect = canvas.getBoundingClientRect();
              
              const handleTouchMove = (moveEvent: TouchEvent) => {
                moveEvent.preventDefault();
                const moveTouch = moveEvent.touches[0];
                const deltaX = ((moveTouch.clientX - startX) / rect.width) * 100;
                const deltaY = ((moveTouch.clientY - startY) / rect.height) * 100;
                onStickerDrag(sticker.id, startStickerX + deltaX, startStickerY + deltaY);
              };
              
              const handleTouchEnd = () => {
                setDraggingStickerId(null);
                document.removeEventListener('touchmove', handleTouchMove);
                document.removeEventListener('touchend', handleTouchEnd);
              };
              
              document.addEventListener('touchmove', handleTouchMove, { passive: false });
              document.addEventListener('touchend', handleTouchEnd);
            }}
          >
            {/* Border - thin black, only when selected */}
            {isSelected && (
              <div className="absolute inset-0 border border-black pointer-events-none" style={{ margin: '-2px' }} />
            )}
            <img
              src={sticker.image}
              alt="Sticker"
              className="max-w-[100px] max-h-[100px] pointer-events-none"
              draggable={false}
            />
            {isSelected && (
              <>
                {/* Delete button */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedStickerId(null);
                    onRemoveSticker(sticker.id);
                  }}
                  className="absolute -top-2 -right-2 w-5 h-5 bg-destructive text-white rounded-full flex items-center justify-center shadow-md hover:bg-destructive/90"
                >
                  <Trash2 className="w-2.5 h-2.5" />
                </button>
                {/* Resize handle */}
                <div
                  className="absolute -bottom-2 -right-2 w-5 h-5 bg-white hover:bg-gray-100 rounded-sm shadow-md cursor-nwse-resize flex items-center justify-center z-10 touch-none border border-black"
                  style={{ touchAction: 'none' }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleStickerResizeStart(e.clientX, e.clientY, sticker);
                  }}
                  onTouchStart={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const touch = e.touches[0];
                    handleStickerResizeStart(touch.clientX, touch.clientY, sticker);
                  }}
                >
                  <ArrowDownRight className="w-3 h-3 text-black" />
                </div>
              </>
            )}
          </div>
        );
      })}
      </div>
    </div>

      {/* Mobile text toolbar - показывается внизу экрана только на мобильных при выбранном тексте */}
      {isMobile && selectedTextId && (
        <div className="fixed bottom-0 left-0 right-0 z-50 flex items-center justify-center gap-2 bg-card/95 backdrop-blur-sm border-t border-border shadow-lg p-3 safe-area-pb">
          {draggingTextId === selectedTextId && (
            <Button
              variant="default"
              size="sm"
              className="h-10 px-4 bg-green-500 hover:bg-green-600 text-white touch-manipulation"
              onClick={(e) => {
                e.stopPropagation();
                setDraggingTextId(null);
              }}
            >
              <Check className="w-4 h-4 mr-1" />
              OK
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            className="h-10 px-4 touch-manipulation"
            onClick={(e) => {
              e.stopPropagation();
              const text = textItems.find(t => t.id === selectedTextId);
              if (text) {
                setDraggingTextId(null);
                setSelectedTextId(null);
                setEditingTextId(text.id);
                setEditingTextValue(text.text);
              }
            }}
          >
            <Pencil className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-10 px-4 text-destructive hover:text-destructive touch-manipulation"
            onClick={(e) => {
              e.stopPropagation();
              setDraggingTextId(null);
              setSelectedTextId(null);
              onRemoveText(selectedTextId);
            }}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      )}

      {/* Mobile image toolbar is now rendered inside ImageEditPortal */}
    </>
  );
};

export default CollageGrid;